import React, { type PropsWithChildren } from 'react';
import { cn } from '@dinewise/utils';
import type { ImageSources } from '@dinewise/utils/node';
import { UserIcon } from '@heroicons/react/24/solid';
import * as RadixAvatar from '@radix-ui/react-avatar';

type AvatarProps = PropsWithChildren<{
  imageSources?: ImageSources | null;
  firstName?: string | null;
  lastName?: string | null;
  initials?: string | null;
  classNames?: {
    root?: string;
    image?: string;
    fallback?: string;
    icon?: string;
  };
  delayMs?: number;
  onClick?: () => void;
}>;

export const Avatar = ({
  children,
  imageSources,
  firstName,
  lastName,
  initials,
  classNames,
  delayMs,
  onClick,
}: AvatarProps) => {
  const initialsFromName =
    firstName && lastName ? `${firstName.charAt(0)}${lastName.charAt(0)}` : '';

  const displayedInitials = (initialsFromName || initials)?.toUpperCase();

  return (
    <RadixAvatar.Root
      {...{ onClick }}
      className={cn(
        'flex size-8 select-none items-center justify-center overflow-hidden rounded-full align-middle',
        classNames?.root,
      )}
    >
      <RadixAvatar.Image
        {...imageSources}
        className={cn('size-full rounded-[inherit] object-cover', classNames?.image)}
        alt="guest-profile-picture"
      />
      <RadixAvatar.Fallback
        {...{ delayMs }}
        className={cn(
          'flex size-full items-center justify-center bg-ande-olive-500 font-serif font-medium text-sm text-white leading-1 shadow-md',
          !displayedInitials && 'bg-transparent text-ande-black',
          classNames?.fallback,
        )}
      >
        {children ?? displayedInitials ?? <UserIcon className={cn('size-12', classNames?.icon)} />}
      </RadixAvatar.Fallback>
    </RadixAvatar.Root>
  );
};

export default Avatar;
